import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import objectSet from 'lodash/set';
import {
  addEdge,
  isNode,
  updateEdge,
} from 'react-flow-renderer';
import { v4 as uuidv4 } from 'uuid';
import { JourneyNodeTypesMap, JourneyTypesMap } from '@usealloy/typegen';

import { transformSdkConfigForSave } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/sdkHelpers';
import { WORKFLOW_TYPES } from '~/constants';
import apiCalls from '~/helpers/apiCalls';
import { ajaxFetch, typhonAjaxFetch } from '~/helpers/api';
import { cloneWorkflow } from '~/flow/CloneWorkflowModal';
import { Actions } from '~/flow/constants';
import { formatExpressions, formatOutputValueRanges } from '~/flow/flowHelper';
import { saveMatrixModels } from '~/flow/matrix-models/services';
import { routes, typhonRoutes } from '~/helpers/routes';
import { ACTION_NODE_TYPES } from '~/journey/constants';
import { ALERT_OUTCOMES, APPLICATION_OUTCOMES, MANUAL_REVIEW_OUTCOME } from '~/journey/outcomes';

import { isActionNode, isLikeWorkflowOrSourceWorkflow } from './customTypeCheckers';
import { MIN_NODE_WIDTH } from './graphService';
import { RESUME_NODE_WIDTH, START_NODE_WIDTH } from './nodes/constants';
import { removeHighlightForNodes } from './nodes/helpers';
import {
  extractNodesAndEdgesFromElements,
} from './react-flow-renderer-upgrade/helpers';
import { getOutgoersRecursively } from './utils/getOutgoersRecursively';
import {
  isReconciliationWorkflowNodeType,
  isRetryOutcomeNode,
  isRootNodeType,
} from './utils/isNode';


/** @typedef {{ currentToken: string; newWorkflowToken: string; }} ClonedWorkflowTokens */


const {
  ACTION,
  ADDNODEFORM,
  DOCV,
  OUTCOME,
  RECONCILIATION_WORKFLOW,
  RESUME,
  RETRY,
  REVIEW,
  SOURCE_WORKFLOW,
  START,
  STEP_UP,
  WORKFLOW,
} = JourneyNodeTypesMap;

// NEW_GRAPH_NODE_TYPES is an array of node types that a new graph is generated
// with; as of now these are the start node and the "Add Node Form" node
// (e.g. the "+" menu node)
const NEW_GRAPH_NODE_TYPES = [ADDNODEFORM, START];
const NODE_TYPES_TO_SAVE = [
  ADDNODEFORM,
  DOCV,
  OUTCOME,
  RECONCILIATION_WORKFLOW,
  RETRY,
  REVIEW,
  SOURCE_WORKFLOW,
  START,
  STEP_UP,
  WORKFLOW,
  ACTION_NODE_TYPES.END_USER,
  ACTION_NODE_TYPES.CUSTOM_REVIEW,
  ACTION_NODE_TYPES.PARENT_REVIEW,
  RESUME,
  ACTION_NODE_TYPES.SERVICE,
  ACTION_NODE_TYPES.EVENT_ACTION,
];

const PORTFOLIO_SW_DEFAULT_TERMINAL_OUTCOME_NAME = 'Portfolio Source Workflow Default Terminal Outcome';

const nextEdgeLayer = 100;

export class WorkflowCloningError extends Error {
  constructor({
    message,
    workflowName,
  }) {
    super(message);
    this.name = 'WorkflowCloningError';
    this.workflowName = workflowName;
  }
}

/** Different Nodes have different widths and we need to set the right width so
  * that the Edges between them are drawn properly
  *
  * @param {Object} node - The node whose width is in question
  * @returns {Number}
  */
function getNodeWidthByType(node) {
  switch (node.type) {
    case RESUME:
      return RESUME_NODE_WIDTH;
    case START:
      return START_NODE_WIDTH;
    default:
      return MIN_NODE_WIDTH;
  }
}

export async function saveJourney({ name, type, typhonDomain }) {
  const result = await typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeys.create}`, {
    method: 'POST',
    body: JSON.stringify({ name, type }),
  });

  return result;
}

export async function saveJourneyVersion({
  active = false,
  changeLog,
  decisions,
  graph,
  isMultiEntity = false,
  journeyNotes,
  journeyToken,
  parentVersion,
  terminalReconciliationWorkflowNodeId,
  typhonDomain,
  versionId,
}) {
  const apiGraph = toAPI({ graph });
  const url = `${typhonDomain}${typhonRoutes.journeys.createVersion(journeyToken)}`;
  const result = await typhonAjaxFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      ...apiGraph,
      active,
      changeLog,
      is_multi_entity: isMultiEntity,
      notes: journeyNotes,
      parent_version: parentVersion,
      reconciliation_conditions: decisions,
      terminal_reconciliation_workflow_node_id: terminalReconciliationWorkflowNodeId,
      version_id: versionId,
    }),
  });
  return result;
}

export async function getMatrixModels(matrixModelTokenAndIds, typhonDomain) {
  return Promise.all(matrixModelTokenAndIds
    .map(async ({ actionId, publicVersionId, matrixModelToken }) => {
      const currentMatrixModel = await typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.matrixModels.get(matrixModelToken, publicVersionId)}`,
        'GET');

      return { ...currentMatrixModel, actionId };
    }));
}

export async function cloneMatrixModels({
  customerId,
  matrixModels,
  typhonDomain,
}) {
  return matrixModels.length && saveMatrixModels(
    typhonDomain,
    matrixModels.map(matrixModel => ({
      ...pick(matrixModel, [
        'actionId',
        'matrix',
        'matrix_data_type',
        'name',
        'x_axis_header',
        'x_axis_header_data_type',
        'y_axis_header',
        'y_axis_header_data_type',
      ]),
      customerId,
    })),
  );
}


export async function createCloneWorkflowPayload(
  currentToken,
  currentWorkflowVersion,
  currentWorkflow,
  type,
  currentWorkflowName,
  services,
  publishedAttributes,
  reasonCodeGroups,
  customerId,
  sandboxPersonas,
  typhonDomain,
  currentCustomerId,
) {
  // Secret action options should not be cloned
  const actionOptions = (currentWorkflow?.actionOptions || [])
    .filter(ao => !ao.isSecret);

  formatExpressions(actionOptions);
  formatOutputValueRanges(actionOptions);
  const matrixModelTokenAndIds = actionOptions
    .filter(({ key }) => key === 'matrix_model_public_version')
    .map(({ actionId, value }) => ({
      actionId,
      publicVersionId: value,
      matrixModelToken: actionOptions
        .find(actionOption => actionOption.actionId === actionId
          && actionOption.key === 'matrix_model_token')?.value,
    }));
  const hasMatrixModels = matrixModelTokenAndIds?.length;
  const currentMatrixModels = hasMatrixModels
    ? await getMatrixModels(matrixModelTokenAndIds, typhonDomain) : [];
  const newActionOptions = actionOptions;

  const actionOptionsWithoutPAHydration = newActionOptions;
  const actionsWithoutPAHydration = currentWorkflow?.actions || [];
  actionsWithoutPAHydration.forEach(action => {
    action.options = {};

    if (action.type === Actions.MATRIX_MODEL) {
      const matrix = currentMatrixModels.find(({ actionId }) => actionId === action.id);
      action.name = matrix.name;
    }

    if (
      publishedAttributes?.length
      && action?.publishedAttributeToken
    ) {
      const associatedPublishedAttribute = publishedAttributes
        .find(({ attributeToken: paToken }) => (
          action?.publishedAttributeToken === paToken
        ));

      if (associatedPublishedAttribute) {
        action.name = associatedPublishedAttribute?.attributeName;
      }
    }

    actionOptionsWithoutPAHydration
      .filter(ao => ao.actionId === action.id)
      .forEach(ao => { action.options[ao.key] = ao; });
  });

  const payload = await ({
    sourceCustomerId: currentCustomerId,
    sourceWorkflowToken: currentToken,
    sourceWorkflowPublicVersion: currentWorkflowVersion,
    workflowName: currentWorkflowName,
    actions: currentWorkflow?.actions || [],
    actionOptions: newActionOptions || [],
    actionOptionsWithoutPAHydration,
    dataRoutes: currentWorkflow?.dataRoutes || [],
    matrixModels: currentMatrixModels || [],
    outcomes: currentWorkflow?.outcomes || [],
    reasonCodeGroups,
    relationships: currentWorkflow?.relationships || [],
    thresholds: currentWorkflow?.thresholds || [],
    tags: currentWorkflow?.tags || [],
    workflowType: type,
    customerId: customerId.toString(),
    sdkConfig: currentWorkflow?.sdkConfig
      ? transformSdkConfigForSave(currentWorkflow?.sdkConfig)
      : {},
    sandboxPersonas,
  });
  return payload;
}

export function cloneAction(action, targetCustomerId, typhonDomain) {
  return apiCalls.journeyActions.cloneJourneyAction({
    typhonDomain,
    actionName: action.name,
    actionType: action.type,
    expSeconds: action.exp_seconds,
    outcomes: action.outcomes,
    actionId: action.journey_action_id,
    targetCustomerId,
    active: true,
    eventTypeTitle: action.event_type_title,
  });
}

export function getClonedActionNode(
  clonedActions,
  node,
) {
  const journeyActionId = node.config.journey_action_id;
  const clonedActionId = clonedActions[journeyActionId]?.journey_action_id;

  if (!clonedActionId) {
    throw new Error(
      `cloning failed for journey action with id ${node.config.journey_action_id}`,
    );
  }

  return {
    ...node,
    config: {
      ...node.config,
      journey_action_id: clonedActionId,
    },
  };
}

export async function getClonedActions(
  journeyActions,
  nodes,
  targetCustomerId,
  typhonDomain,
) {
  return nodes.reduce(async (clonedActionsPromise, node) => {
    if (!isActionNode(node.type)) return clonedActionsPromise;

    const clonedActions = await clonedActionsPromise;
    const journeyActionId = node.config.journey_action_id;

    if (!clonedActions[journeyActionId]) {
      const sourceAction = journeyActions[journeyActionId];
      clonedActions[journeyActionId] = await cloneAction(
        sourceAction, targetCustomerId, typhonDomain,
      );
    }

    return clonedActions;
  }, {});
}

export function getClonedWorkflowNode(
  clonedWorkflowTokens,
  node,
  featureFlags,
) {
  const isEOJReconEnabled = Boolean(featureFlags?.reconWorkflowInGraph);
  const newToken = clonedWorkflowTokens?.find(tokenObj => (
    tokenObj.currentToken === node.config.workflow_token
  ))?.newWorkflowToken;

  if (!newToken) {
    throw new Error(
      `cloning failed for workflow ${node.config.workflow_token}`,
    );
  }

  return {
    ...node,
    config: {
      ...node.config,
      application_token: newToken,
      workflow_token: newToken,
      source_workflow_token: node.config.workflow_token,
      application_version_id: 1,
      workflow_version_id: 1,
      ...(
        isEOJReconEnabled || !isReconciliationWorkflowNodeType(node.type)
          ? { workflow_current_version: 1 }
          : {}
      ),
    },
  };
}

/**
 * Clones workflows using legacy endpoints and returns a bundle with the previous and
 * newly created workflow token
 * @returns {Promise<Array<ClonedWorkflowTokens>>}
 */
export async function getClonedWorkflowTokens(
  featureFlags,
  nodes,
  shouldCloneSandboxPersonas,
  targetCustomerId,
  typhonDomain,
  updatedSandboxPersonas,
  currentCustomerId,
) {
  const getCurrentWorkflowUrl = (token, id) => (
    `${routes.applications.versions.data(token, id)}`
  );


  const currentWorkflowTokens = nodes
    .filter(node => isLikeWorkflowOrSourceWorkflow(node.type))
    ?.map(node => node.config.workflow_token);
  const currentWorkflowTokensSet = new Set(currentWorkflowTokens);

  const cloneWorkflowPayloads = await Promise.all(
    [...currentWorkflowTokensSet].map(async currentToken => {
      const data = await ajaxFetch(
        `${routes.applications.detailData(currentToken)}`,
      );

      const currentWorkflowNode = nodes.find(
        node => (
          isLikeWorkflowOrSourceWorkflow(node.type)
          && node.config.workflow_token === currentToken
        ),
      );
      const currentWorkflowName = currentWorkflowNode?.config?.name;
      /**
       * `workflow_current_version` is not currently set for recon workflow nodes
       * so we need to have a fallback here so we're not passing `null` for the
       * version to fetch below
       */
      const currentWorkflowVersion = currentWorkflowNode?.config?.workflow_current_version
        ?? data?.application?.active_public_version;

      const [currentWorkflow, propsData] = await Promise.all([
        ajaxFetch(getCurrentWorkflowUrl(
          currentToken,
          currentWorkflowVersion,
        )),
        ajaxFetch(`${routes.applications.versions.props(currentToken)}`),
      ]);
      const sandboxPersonasToClone = updatedSandboxPersonas
        ? updatedSandboxPersonas[currentToken]
        : propsData?.sandboxPersonas;

      const cloneWorkflowPayload = await createCloneWorkflowPayload(
        currentToken,
        currentWorkflowVersion,
        currentWorkflow,
        data?.application?.type,
        currentWorkflowName,
        propsData?.services,
        propsData?.publishedAttributes || [],
        propsData?.reasonCodeGroups,
        targetCustomerId,
        shouldCloneSandboxPersonas
          ? sandboxPersonasToClone
          : undefined,
        typhonDomain,
        currentCustomerId,
      );

      return {
        payload: cloneWorkflowPayload,
        token: currentToken,
      };
    }));

  const tokens = await cloneWorkflowPayloads.reduce(
    async (clonedWorkflowTokensPromise, currentWorkflow) => {
      const clonedWorkflowTokens = await clonedWorkflowTokensPromise;

      const response = await cloneWorkflow(
        typhonDomain,
        currentWorkflow.payload,
        currentCustomerId,
        true,
      ).then(resp => resp.json());

      if (response.result === 'error') {
        return Promise.reject(
          /**
            * To avoid multiple levels of try/catches and error checking with all the promises
            * used during journey cloning, we throw a custom error here so that journey.jsx
            * can check for an instance of `WorkflowCloningError` to be able to show a more
            * detailed message about which workflow failed to clone while cloning the journey.
            */
          new WorkflowCloningError(
            {
              message: response.message,
              workflowName: currentWorkflow.payload.workflowName,
            },
          ),
        );
      }

      return clonedWorkflowTokens.concat({
        currentToken: currentWorkflow.token,
        newWorkflowToken: response && response.workflow_token,
      });
    },
    Promise.resolve([]),
  );

  return tokens;
}


/**
 * @param {Object} args
 * @param {number} args.agentId
 * @param {Set<string>} args.backtestingWorkflowTokens
 * @param {number} args.currentCustomerId
 * @param {Record<string, any>} args.featureFlags
 * @param {any} args.journeyActions
 * @param {Array<any>} args.nodes
 * @param {boolean} args.shouldCloneSandboxPersonas
 * @param {number} args.targetCustomerId
 * @param {string} args.targetCustomerOrganizationNameSlug
 * @param {string} args.typhonDomain
 * @param {any} args.updatedSandboxPersonas
 * @param {Record<string, string>} args.workflowRenamingMap
 * @returns
 */
export async function cloneNodes({
  agentId,
  backtestingWorkflowTokens,
  currentCustomerId,
  featureFlags,
  journeyActions,
  nodes,
  shouldCloneSandboxPersonas,
  targetCustomerId,
  targetCustomerOrganizationNameSlug,
  typhonDomain,
  updatedSandboxPersonas,
  workflowRenamingMap,
}) {
  /** @type {Array<ClonedWorkflowTokens>} */
  let clonedWorkflowTokens;
  if (featureFlags.typhonJourneyClone) {
    const workflowsToClone = await buildWorkflowsToClone({ nodes, backtestingWorkflowTokens });
    clonedWorkflowTokens = await cloneWorkflowsUsingTyphon({
      agentId,
      shouldCloneSandboxPersonas,
      typhonDomain,
      targetOrganizationSlug: targetCustomerOrganizationNameSlug,
      workflowsToClone,
      workflowRenamingMap,
    });
  } else {
    clonedWorkflowTokens = await getClonedWorkflowTokens(
      featureFlags,
      nodes,
      shouldCloneSandboxPersonas,
      targetCustomerId,
      typhonDomain,
      updatedSandboxPersonas,
      currentCustomerId,
    );
  }

  const clonedActions = await getClonedActions(
    journeyActions, nodes, targetCustomerId, typhonDomain,
  );

  return nodes.reduce(
    (acc, node) => {
      if (isActionNode(node.type)) {
        const clonedActionNode = getClonedActionNode(clonedActions, node);
        acc.push(clonedActionNode);
      } else if (isLikeWorkflowOrSourceWorkflow(node.type)) {
        const clonedWorkflowNode = getClonedWorkflowNode(
          clonedWorkflowTokens,
          node,
          featureFlags,
        );
        acc.push(clonedWorkflowNode);
      } else {
        acc.push(node);
      }

      return acc;
    },
    [],
  );
}

/**
 * @param {Object} args
 * @param {any} args.actionsById
 * @param {number} args.agentId
 * @param {Set<string>} args.backtestingWorkflowTokens
 * @param {number} args.currentCustomerId
 * @param {number} args.customerId
 * @param {Record<string, any>} args.featureFlags
 * @param {any} args.graph
 * @param {string} args.journeyToken
 * @param {boolean} args.isMultiEntity
 * @param {string} args.name
 * @param {any} args.reconciliationConditions
 * @param {boolean} args.shouldCloneSandboxPersonas
 * @param {string} args.targetCustomerOrganizationNameSlug
 * @param {number} args.terminalReconciliationWorkflowNodeId
 * @param {string} args.type
 * @param {string} args.typhonDomain
 * @param {any} args.updatedSandboxPersonas
 * @param {number} args.versionId public version of the journey version to clone
 * @param {Record<string, string>} args.workflowRenamingMap
 */
export async function cloneJourneyVersion({
  actionsById,
  agentId,
  backtestingWorkflowTokens,
  currentCustomerId,
  customerId,
  featureFlags,
  graph,
  isMultiEntity = false,
  journeyToken,
  name,
  reconciliationConditions,
  shouldCloneSandboxPersonas,
  targetCustomerOrganizationNameSlug,
  terminalReconciliationWorkflowNodeId,
  type,
  typhonDomain,
  updatedSandboxPersonas,
  versionId,
  workflowRenamingMap,
}) {
  const apiGraph = toAPI({ graph });
  const url = `${typhonDomain}${typhonRoutes.journeys.cloneVersion(journeyToken, versionId)}`;
  const nodes = customerId === currentCustomerId
    ? apiGraph.nodes
    : await cloneNodes({
      agentId,
      backtestingWorkflowTokens,
      currentCustomerId,
      featureFlags,
      journeyActions: actionsById,
      nodes: apiGraph.nodes,
      workflowRenamingMap,
      shouldCloneSandboxPersonas,
      targetCustomerId: customerId,
      targetCustomerOrganizationNameSlug,
      typhonDomain,
      updatedSandboxPersonas,
    });
  const apiGraphWithClonedWorkflows = {
    ...apiGraph,
    is_multi_entity: isMultiEntity,
    nodes,
    reconciliationConditions: reconciliationConditions || null,
    terminal_reconciliation_workflow_node_id: terminalReconciliationWorkflowNodeId,
  };

  const result = await typhonAjaxFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      ...apiGraphWithClonedWorkflows,
      customerId,
      name,
      type,
    }),
  });

  return result;
}

export async function updateJourneyActiveVersion({
  activeVersionId,
  journeyToken,
  typhonDomain,
}) {
  const url = `${typhonDomain}${typhonRoutes.journeys.get(journeyToken)}`;
  return typhonAjaxFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({ active_version_id: activeVersionId }),
    silent: true,
  });
}

export function toAPI({ graph }) {
  const { edges, nodes } = extractNodesAndEdgesFromElements(graph);
  const result = {
    nodes: [],
    edges: [],
    start_node_id: '',
  };
  let graphCpy = cloneDeep(graph);

  // Dont save purely 'ui' nodes, this removes any edges that might exist
  // to/from these nodes as well
  const nodesToRemove = [];

  graphCpy.forEach(element => {
    if (isNode(element)) {
      if (!NODE_TYPES_TO_SAVE.includes(element.type) || isRetryOutcomeNode(element)) {
        nodesToRemove.push(element);
      }

      if (
        element.data?.config?.workflowType === WORKFLOW_TYPES.RECONCILIATION
          && element.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
          && element.data?.config?.isEOJRecon
      ) {
        nodesToRemove.push(...getOutgoersRecursively(element, nodes, edges));
      }
    }
  });

  const nodeIdsToRemove = nodesToRemove.map(n => n.id);
  const edgesToRemove = graphCpy.filter(element => !!nodeIdsToRemove.find(
    id => id === element.target || id === element.source,
  ));

  graphCpy = graphCpy.filter(element => !nodeIdsToRemove.includes(element.id));
  const edgeIdsToRemove = edgesToRemove.map(edge => edge.id);

  graphCpy.forEach(element => {
    if (isNode(element)) {
      result.nodes.push(buildNodeForAPI(element));
      if (isRootNodeType(element.type)) {
        result.start_node_id = element.id;
      }
    } else if (!edgeIdsToRemove.includes(element.id)) {
      result.edges.push(buildEdgeForAPI(element));
    }
  });

  edgesToRemove.forEach(element => {
    if (element.data?.condition?.toLowerCase() === RETRY) {
      const retryNode = result.nodes.find(
        potentialRetryNode => (
          potentialRetryNode.id === element.source
          && potentialRetryNode.type === RETRY
        ),
      );

      const originalNodeToRetry = (
        retryNode
          ? result.nodes.find(potentialOutcomeNode => (
            potentialOutcomeNode.id === retryNode.config?.elementToRetryId
          ))
          : undefined
      );

      element.target = originalNodeToRetry?.id;

      result.edges.push(buildEdgeForAPI(element));
    }
  });
  return result;
}

export function fromAPI({
  actionNodes,
  data,
  position,
  featureFlags = {},
}) {
  // eslint-disable-next-line no-underscore-dangle
  const isJourneyAlertType = (data?._embedded?.journey.type === 'alert' || data?.type === 'alert');

  const investigationConfigEnabled = Boolean(featureFlags?.ongoingJourneyInvestigationsConfig);
  const applicationAndAlertOpeningConfigEnabled = Boolean(
    featureFlags?.applicationAndAlertOpeningJourneyConfig,
  );

  let isStartEdgeEditable = false;
  const nodes = data.nodes.map(node => {
    if (node.type === JourneyNodeTypesMap.START) {
      isStartEdgeEditable = true;
    }

    if (node.type === ACTION) {
      const action = actionNodes.find(actionData => {
        actionData.journey_action_id = Number(actionData.journey_action_id);
        return actionData.journey_action_id === node.config.journey_action_id;
      });

      node.config = {
        ...node.config,
        event_type_title: action?.event_type_title,
        name: action?.name,
      };
      node.isArchived = !!action?.archive_at;
      node.type = action?.type;
      node.outcomes = action?.outcomes;
    }

    // ===== PORTFOLIO DEFAULT OUTCOME Preprocessing ==========================
    if (node.name === PORTFOLIO_SW_DEFAULT_TERMINAL_OUTCOME_NAME) {
      node.config.isPEDefaultOutcome = true;
    }

    // ===== TERMINAL/EOJ/MJ RECON NODE Preprocessing ==========================
    if (
      data.terminal_reconciliation_workflow_node_id === node.id
        && node.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
    ) {
      node.config = {
        ...node.config,
        isEOJRecon: true,
        isMJRecon: false,
      };
    } else if (
      node.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
      && data.terminal_reconciliation_workflow_node_id !== node.id
    ) {
      node.config = {
        ...node.config,
        isEOJRecon: false,
        isMJRecon: true,
      };
    }
    // =EOF= TERMINAL/EOJ/MJ RECON NODE Preprocessing ==========================

    return buildNodeForReactFlow({
      data: {
        ...node,
        startNode: node.id === data.start_node_id,
      },
      position,
      ...pickBy(
        node,
        (value, key) => (
          ['id', 'name', 'type'].includes(key) && !isEmpty(value)
        ),
      ),
      width: getNodeWidthByType(node),
    });
  });

  // ===== Mid-Journey Recon Preprocessing =====================================
  let resumeNodesIds = [];

  if (nodes.some(node => node.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW)) {
    resumeNodesIds = nodes.filter(node => node.type === 'resume').map(node => node.id);
  }
  // =EOF= Mid-Journey Recon Preprocessing =====================================

  const edges = data.edges.map(edge => {
    const { condition, config } = edge;
    const isStartNode = data.start_node_id === edge.source;
    const isEditableStartEdge = isStartEdgeEditable && isStartNode;
    const isConfigEnabled = (isStartNode
      && (investigationConfigEnabled || applicationAndAlertOpeningConfigEnabled)
    );

    const isResumeNodeEdge = resumeNodesIds.includes(edge.source);
    const newEdge = {
      data: {
        // This should only be used for React Flow. Other than this we should
        // use the config object to check if an edge OpensAlerts
        config,
        condition,
        ...(isResumeNodeEdge ? { disabled: true } : {}),
      },
      edgeLabel: (isEditableStartEdge || !isStartNode || isConfigEnabled)
        ? condition : null,
      id: uuidv4(),
      label: (isEditableStartEdge || !isStartNode || isConfigEnabled)
        ? condition : null,
      source: edge.source,
      sourceHandle: uuidv4(),
      target: edge.target,
      targetHandle: uuidv4(),
      // TODO: Allow API to handle this logic and remove the source check
      type: (
        edge.type
          || (
            (isEditableStartEdge || isResumeNodeEdge)
              ? 'input'
              : 'simple'
          )
      ),
    };
    // Since we are doing some manipulation of the graph to display the retry
    // node we need to do some translation here when we get the data from the
    // API
    if (condition?.toLowerCase() === RETRY) {
      // We need to check that the source node is a Retry node because some
      // users have defined a "Retry" outcome for their own Workflow nodes
      const isRetryNode = (
        nodes.find(node => node.id === edge.source)?.type === RETRY
      );

      if (isRetryNode) {
        const retryNodeId = uuidv4();
        const retryNode = nodes.find(
          node => node.id === edge.source && node.type === RETRY,
        );

        objectSet(retryNode, 'data.config.elementToRetryId', edge.target);

        nodes.push(
          buildNodeForReactFlow({
            position,
            data: { config: { outcome: 'Retry' } },
            id: retryNodeId,
            name: 'Retry',
            type: JourneyNodeTypesMap.OUTCOME,
          }),
        );

        newEdge.target = retryNodeId;
      }
    }

    return newEdge;
  });

  let newEdges = [...edges];
  let newNodes = [...nodes];

  nodes.forEach(node => {
    if (node.type === REVIEW) {
      node.data.outcomes = isJourneyAlertType
        ? ['Suspicious', 'Dismissed']
        : ['Approved', 'Denied'];
    } else if (!node?.data?.config?.journey_action_id) {
      return;
    }

    ({ edges: newEdges, nodes: newNodes } = addNodeWithOutcomes({
      edges: newEdges,
      journeyType: isJourneyAlertType ? JourneyTypesMap.ALERT : JourneyTypesMap.APPLICATION,
      nodes: newNodes,
      outcomes: node.data.outcomes,
      sourceId: node.id,
      position: {
        x: node.position.x + nextEdgeLayer,
        y: node.position.y,
      },
    }));
  });

  return { edges: newEdges, nodes: newNodes };
}

export function buildEdgeForReactFlow({
  data, label, overrides, source, target,
}) {
  return {
    data,
    label,
    source,
    target,
    hidden: false,
    id: uuidv4(),
    sourceHandle: uuidv4(),
    targetHandle: uuidv4(),
    type: 'simple',
    ...overrides,
  };
}

export function buildNodeForReactFlow({
  data = {}, id = null, name, position, type, width = MIN_NODE_WIDTH,
}) {
  const defaultNodeId = uuidv4();
  const newNode = {
    id: id || defaultNodeId,
    data: {
      config: {},
      label: `${name} - ${type}`,
      ...(
        (type === ACTION_NODE_TYPES.CUSTOM_REVIEW || type === ACTION_NODE_TYPES.PARENT_REVIEW)
        && { name }
      ),
      hide: false,
      ...data,
    },
    type,
    // This gets positioned at 0,0 first, before calling layout and putting it
    // in the right place; need a way to get it on the dom but not visible
    position,
    width,
  };

  newNode.data = { ...newNode.data, id: newNode.id };

  return newNode;
}

export function updateNodeToPEDefaultOutcome({
  node = {},
}) {
  const name = PORTFOLIO_SW_DEFAULT_TERMINAL_OUTCOME_NAME;
  const type = JourneyNodeTypesMap.OUTCOME;
  return {
    ...node,
    name,
    type,
    data: {
      ...node.data,
      label: name,
      config: {
        ...(node.data?.config ?? {}),
        outcome: 'Dismissed',
        depth: 1,
        isPEDefaultOutcome: true,
      },
    },
  };
}

export function addNodeWithEdge({
  data,
  edgeLabel,
  edgeOverrides = {},
  edges,
  name,
  nodes,
  position,
  sourceId,
  type,
  width = MIN_NODE_WIDTH,
}) {
  const exists = edges.find(edge => {
    const sameSource = edge.source === sourceId;
    const sameLabel = edge.label === edgeLabel;
    return sameSource && sameLabel;
  });

  if (exists) {
    return { edges, nodes };
  }

  const newNode = buildNodeForReactFlow({
    name,
    position,
    type,
    width,
    data: { ...data, sourceId },
  });

  const newEdges = addEdge(buildEdgeForReactFlow({
    data,
    label: edgeLabel,
    overrides: edgeOverrides,
    source: sourceId,
    target: newNode.id,
  }), edges);

  return { edges: newEdges, nodes: [...nodes, newNode] };
}

export function updateEdgeTargetAndRemoveOldTarget({
  elements,
  newTargetId,
  oldTargetId,
  sourceId,
}) {
  const edge = elements.find(
    el => el.source === sourceId && el.target === oldTargetId,
  );

  if (!edge) {
    return elements;
  }

  const newConnection = {
    source: sourceId,
    sourceHandle: uuidv4(),
    target: newTargetId,
    targetHandle: uuidv4(),
  };

  const updatedElements = updateEdge(
    edge,
    newConnection,
    elements,
  );

  const { edges, nodes } = (
    extractNodesAndEdgesFromElements(
      removeHighlightForNodes(
        updatedElements.filter(element => element.id !== oldTargetId),
      ),
    )
  );

  // return the "saveable" elements only
  return { edges, nodes };
}

export function addNodeWithOutcomes({
  allEojReconOutcomes = [],
  data = {},
  edges,
  journeyType,
  name = undefined,
  nodes,
  outcomes,
  position = {},
  sourceId,
  type = ADDNODEFORM,
  width = MIN_NODE_WIDTH,
}) {
  let newEdges = [...edges];
  let newNodes = [...nodes];

  outcomes?.forEach(outcome => {
    const { isEOJRecon } = data?.config ?? {};
    const isEOJManualReviewNode = (
      isEOJRecon && type === JourneyNodeTypesMap.OUTCOME && outcome === MANUAL_REVIEW_OUTCOME
    );

    ({ edges: newEdges, nodes: newNodes } = addNodeWithEdge({
      position,
      sourceId,
      width,
      ...(isEOJManualReviewNode ? { type: JourneyNodeTypesMap.REVIEW } : { type }),
      data: {
        condition: outcome,
        ...data,
        ...(type === JourneyNodeTypesMap.OUTCOME && { config: { outcome, ...data.config } }),
        /**
         * With the introduction of Ongoing MEJ, a scenario that was not previously accounted for was if an EOJ
         * Recon Workflow decisions to a Manual Review outcome. This behavior automatically adds a Manual Review
         * node to the Journey graph. If we were to use validation to account for this, it would require there to
         * be an Investigation Opening Edge at some point in every possible traversal of the graph, which is not
         * a desired scenario.
         *
         * To avoid this, when a Manual Review outcome is reached from the EOJ Recon node, we will check if there
         * is already an Investigation open for the Alert. If there is not, we will automatically open one before
         * the review step. This manual configuation of the node will allow this behavior to be displayed in the
         * Journey builder, since the nodes/edges after the EOJ Recon node are not actually saved to the
         * Journey graph.
         */
        ...(journeyType === JourneyTypesMap.ALERT && isEOJManualReviewNode && {
          config: {
            opensInvestigation: true,
            investigationType: 'Compliance',
            isTemporal: true,
            ...data?.config,
          },
        }),
      },
      edgeLabel: outcome,
      edges: newEdges,
      name: name ?? `Add new node for ${outcome}`,
      nodes: newNodes,
    }));

    if (isEOJManualReviewNode) {
      /**
       * It's possible when swapping EOJ recon WFs/versions that we only need to add some
       * of the outcomes as nodes off of the recon node and edges off of the Manual Review
       * node, if there is one. But if we are adding a new Manual Review node off of the EOJ
       * recon node, we'll need to add all of the recon WF outcomes as outcomes of that
       * Manual Review node.
       */
      const outcomesToAdd = allEojReconOutcomes?.length > outcomes?.length
        ? allEojReconOutcomes
        : outcomes;

      const defaultManualReviewOutcomes = journeyType === JourneyTypesMap.APPLICATION
        ? APPLICATION_OUTCOMES
        : ALERT_OUTCOMES;

      /**
       * `Manual Review` is an allowed outcome off of the EOJ Recon WF node, but if we pass
       * it in to the util here it will create an additional manual review outcome/node off
       * of the one manual review node we want to allow off of EOJ Recon
       *
       * Include `APPLICATION_OUTCOMES` or `ALERT_OUTCOMES` to ensure Approved/Denied or
       * Dismissed/Suspicious are always included for post-EOJ recon manual review node outcomes
       * based on Journey type
       */
      const eojOutcomes = [...(new Set([...outcomesToAdd, ...defaultManualReviewOutcomes]))].filter(
        eojOutcome => eojOutcome?.toLowerCase() !== MANUAL_REVIEW_OUTCOME.toLowerCase(),
      );

      /**
       * addNodeWithEdge above will always add the Node and then the Edge to the
       * elements array so since we want the Node we can grab it like this
       */
      const node = newNodes[newNodes.length - 1];
      ({ edges: newEdges, nodes: newNodes } = addNodeWithOutcomes({
        data: { config: { isEOJRecon: true, isTemporal: true } },
        edges: newEdges,
        journeyType,
        nodes: newNodes,
        outcomes: eojOutcomes?.length
          ? eojOutcomes
          : defaultManualReviewOutcomes,
        position,
        sourceId: node.id,
        type: JourneyNodeTypesMap.OUTCOME,
      }));
    }
  });

  return { edges: newEdges, nodes: newNodes };
}

export function buildNodeForAPI(element) {
  return {
    config: { ...element.data.config },
    ...pick(element, ['id', 'type', 'name']),
    ...(element.data?.name && { name: element.data?.name }),
  };
}

export function buildEdgeForAPI(element) {
  return {
    source: element.source,
    target: element.target,
    condition: element?.data?.condition || '',
    config: element?.data?.config,
  };
}

/** Check if the only nodes in the graph are the 2 nodes that all new graphs
  * start with: the 'start' node and the 'addNodeForm' node
  * @param {Array} graph - An array of React Flow Nodes
  * @returns {Boolean}
  */
export function isNewGraph(graph = []) {
  return graph.filter(node => !NEW_GRAPH_NODE_TYPES.includes(node)).length === 0;
}

/** dontSave checks if the user shouldn't be able to save the current Journey
  * graph as it is; this is true for fresh/new graphs and graphs without any
  * nodes included in NODE_TYPES_TO_SAVE
  * @param {Array} graph - An array of React Flow node elements
  * @returns {Boolean}
  */
export function dontSave(graph = []) {
  if (isEmpty(graph) || isNewGraph(graph)) {
    return true;
  }

  for (let i = 0; i < graph.length; i += 1) {
    const elem = graph[i];
    // TODO: probably refactor this to be a property of the component / data itself,
    //       but that seems messier right now
    if (isNode(elem)) {
      if (elem.type === WORKFLOW && (
        !elem.data.config.application_version_id || !elem.data.config.application_token
      )) {
        return true;
      }
      if (NODE_TYPES_TO_SAVE.includes(elem.type)) {
        return false;
      }
    }
  }
  return true;
}


// ========= Typhon Cloning
/** @typedef {{ workflowToken: string; version: number; }} WorkflowTokenAndVersion */
/** @typedef {WorkflowTokenAndVersion & { isBacktest: boolean }} WorkflowToClone */

/**
 * returns an array of unique workflow tokens and their corresponding version in the journey
 * note: the UI builder makes it impossible to have more than one version of the same workflow
 * within a journey, so there is no need to deduplicate using the version - the token is enough.
 * if a node has a missing version number (as it happens with recon nodes), the version number
 * is fetched
 * @param {Array<any>} nodes
 * @returns {Promise<Array<WorkflowTokenAndVersion>>} unique pairs of token and version for
 * workflows in the journey nodes
 */
export async function getUniqueWorkflowsTokenAndVersionFromJourneyNodes(nodes) {
  /** @type Set<string> */
  const seenWorkflowTokens = new Set();


  const workflowTokensAndVersionNumbers = (await Promise.all(nodes
    .filter(node => isLikeWorkflowOrSourceWorkflow(node.type))
    .map(async node => {
      const workflowToken = node.config.workflow_token;
      let version = node.config.workflow_current_version;

      if (seenWorkflowTokens.has(workflowToken)) {
        return null;
      }
      seenWorkflowTokens.add(workflowToken);

      if (!version) {
        const workflowDetails = await ajaxFetch(`${routes.applications.detailData(workflowToken)}`);
        version = workflowDetails.application.active_public_version;
      }

      return {
        version,
        workflowToken,
      };
    }),
  )).filter(Boolean);

  return workflowTokensAndVersionNumbers;
}


/**
 * @param {Object} args
 * @param {Array<any>} args.nodes
 * @param {Set<string>} args.backtestingWorkflowTokens
 * @returns {Array<WorkflowToClone>} bundle for workflows to clone
 */
async function buildWorkflowsToClone({
  nodes,
  backtestingWorkflowTokens,
}) {
  const workflowTokenAndVersion = await getUniqueWorkflowsTokenAndVersionFromJourneyNodes(nodes);
  return workflowTokenAndVersion.map(({ workflowToken, version }) => ({
    workflowToken,
    version,
    isBacktest: backtestingWorkflowTokens.has(workflowToken),
  }));
}


/**
 * Clones workflows using the typhon endpoints.
 * This function's return type and scope is modeled after the existing legacy
 * clone function `getClonedWorkflowTokens`
 *
 * @param {Object} args
 * @param {number} args.agentId
 * @param {boolean} args.shouldCloneSandboxPersonas
 * @param {string} args.targetOrganizationSlug
 * @param {string} args.typhonDomain
 * @param {Array<WorkflowToClone>} args.workflowsToClone
 * @param {Record<string, string>} [args.workflowRenamingMap]
 *
 * @returns {Promise<ClonedWorkflowTokens[]>}
 */
async function cloneWorkflowsUsingTyphon({
  agentId,
  shouldCloneSandboxPersonas,
  targetOrganizationSlug,
  typhonDomain,
  workflowsToClone,
  workflowRenamingMap,
}) {
  /** @type ClonedWorkflowTokens[] */
  const workflowTokenPairs = await Promise.all(
    workflowsToClone.map(async workflowToClone => {
      /**
       * Typhon cloning requires 2 steps:
       * 1. generate a schema
       * 2. perform the clone
       */
      try {
        const schemaGenerationResponse = await apiCalls.cloning.schema({
          applicationToken: workflowToClone.workflowToken,
          typhonDomain,
          includeSandboxPersonas: shouldCloneSandboxPersonas,
          versionId: workflowToClone.version,
          workflowsToRename: workflowRenamingMap,
          backtesting: workflowToClone.isBacktest,
        });

        if (schemaGenerationResponse.error || !schemaGenerationResponse.policy_token) {
          throw new WorkflowCloningError({
            message: schemaGenerationResponse.error || 'Failed to generate Typhon clone schema',
            workflowName: workflowToClone.workflowToken,
          });
        }
        const cloneResponse = await apiCalls.cloning.clone({
          agentId,
          cloningType: 'workflow',
          policyToken: schemaGenerationResponse.policy_token,
          targetOrganizationSlug,
          typhonDomain,
        });

        return {
          currentToken: workflowToClone.workflowToken,
          newWorkflowToken: cloneResponse.cloning_result.workflow_token,
        };
      } catch (error) {
        if (error instanceof WorkflowCloningError) throw error;
        throw new WorkflowCloningError({
          message: error.message,
          workflowName: workflowToClone.workflowToken,
        });
      }
    }));

  return workflowTokenPairs;
}
